import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { BetInput } from './BetInput';
import { TDispatch } from '../../../../redux';
import { changeAuctionBet, ECheckServiceStatus, TChangeAuctionBetSource } from '../../../../redux/reducers';
import {
  selectCurrentBet,
  selectIsAuctionSelected,
  selectPreviousBets,
  selectNextBets,
} from '../../../../selectors/auction';
import { parseBet } from './utils/parseBet';
import { selectCheckServiceStatus } from '../../../../selectors/checkoutInfo';
import { useAuctionState } from './hooks/useAuctionState';
const debounce = require('lodash/debounce');

const saveBetToStore = debounce(
  (dispatch: TDispatch, bet: number, source: TChangeAuctionBetSource) => dispatch(changeAuctionBet({ bet, source })),
  700,
);

export const BetInputContainer = () => {
  const defaultBet = useSelector(selectCurrentBet);
  const previousBets = useSelector(selectPreviousBets);
  const nextBets = useSelector(selectNextBets);
  const status = useSelector(selectCheckServiceStatus);
  const isAuctionSelected = useSelector(selectIsAuctionSelected);

  const dispatch = useDispatch<TDispatch>();

  const { currentBet, previousBet, nextBet, updateBet, decreaseBet, increaseBet } = useAuctionState({
    defaultBet,
    nextBets,
    previousBets,
  });

  const loading = status === ECheckServiceStatus.pending && isAuctionSelected;

  const handleChange = React.useCallback(
    (value: string) => {
      const newBet = parseBet(value);

      if (newBet === currentBet || loading) {
        return;
      }

      updateBet(newBet);
      saveBetToStore(dispatch, newBet, 'input');
    },
    [dispatch, currentBet, loading, updateBet],
  );

  const handlePreviousClick = React.useCallback(() => {
    if (previousBet === null) {
      return;
    }

    decreaseBet();
    saveBetToStore(dispatch, previousBet, 'minus');
  }, [previousBet, dispatch, decreaseBet]);

  const handleNextClick = React.useCallback(() => {
    if (nextBet === null) {
      return;
    }

    increaseBet();
    saveBetToStore(dispatch, nextBet, 'plus');
  }, [nextBet, dispatch, increaseBet]);

  return (
    <BetInput
      value={String(currentBet)}
      loading={loading}
      previousDisabled={previousBet === null || loading}
      nextDisabled={nextBet === null || loading}
      onChange={handleChange}
      onPreviousClick={handlePreviousClick}
      onNextClick={handleNextClick}
    />
  );
};
